import { useMemo, useState } from 'react';
import Modal from '../../../shared/components/modals/Modal';

const NUMERIC_COLUMNS = new Set([
  'active_devices',
  'coverage_expiring',
  'no_coverage',
  'coverage_expired',
  'warranty_count',
  'lamc_count',
  'camc_count',
  'inhouse_count',
]);

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const CoverageCount = ({ value, tone }) => {
  const n = toCount(value);
  const className = n === 0
    ? 'sa-coverage-modal__count sa-coverage-modal__count--zero'
    : `sa-coverage-modal__count sa-coverage-modal__count--${tone}`;
  return <span className={className}>{n}</span>;
};

export default function AssetCoverageByLocationModal({
  isOpen,
  onClose,
  rows = [],
  onSelectLocation,
}) {
  const [sortColumn, setSortColumn] = useState('attention');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'location' ? 'asc' : 'desc');
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return <span className="sa-coverage-modal__sort sa-coverage-modal__sort--idle">⇅</span>;
    }
    return (
      <span className="sa-coverage-modal__sort">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const sortedRows = useMemo(() => {
    if (!rows.length) return [];
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortColumn === 'location') {
        return direction * String(a.location_name || '').localeCompare(String(b.location_name || ''));
      }
      if (sortColumn === 'attention') {
        const attention = (row) => (
          toCount(row.coverage_expired) * 1000
          + toCount(row.coverage_expiring) * 10
          + toCount(row.no_coverage)
        );
        const diff = attention(a) - attention(b);
        if (diff !== 0) return direction * diff;
        return String(a.location_name || '').localeCompare(String(b.location_name || ''));
      }
      if (NUMERIC_COLUMNS.has(sortColumn)) {
        return direction * (toCount(a[sortColumn]) - toCount(b[sortColumn]));
      }
      return 0;
    });
  }, [rows, sortColumn, sortDirection]);

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    active_devices: acc.active_devices + toCount(row.active_devices),
    coverage_expiring: acc.coverage_expiring + toCount(row.coverage_expiring),
    no_coverage: acc.no_coverage + toCount(row.no_coverage),
    coverage_expired: acc.coverage_expired + toCount(row.coverage_expired),
    warranty_count: acc.warranty_count + toCount(row.warranty_count),
    lamc_count: acc.lamc_count + toCount(row.lamc_count),
    camc_count: acc.camc_count + toCount(row.camc_count),
    inhouse_count: acc.inhouse_count + toCount(row.inhouse_count),
  }), {
    active_devices: 0,
    coverage_expiring: 0,
    no_coverage: 0,
    coverage_expired: 0,
    warranty_count: 0,
    lamc_count: 0,
    camc_count: 0,
    inhouse_count: 0,
  }), [rows]);

  const expiredLocationCount = rows.filter((row) => toCount(row.coverage_expired) > 0).length;
  const locationCount = rows.length;

  const sortHeader = (column, label, numeric = false) => (
    <th
      className={`sortable${numeric ? ' num' : ''}`}
      onClick={() => handleSort(column)}
    >
      {label}{renderSortIndicator(column)}
    </th>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Asset coverage by location" size="large">
      <div className="sa-coverage-modal">
        {expiredLocationCount > 0 ? (
          <div className="sa-coverage-modal__banner">
            {expiredLocationCount} of {locationCount} location{locationCount !== 1 ? 's' : ''} {expiredLocationCount === 1 ? 'has' : 'have'} expired coverage
          </div>
        ) : (
          <div className="sa-coverage-modal__meta">
            {locationCount} location{locationCount !== 1 ? 's' : ''} · active devices only
          </div>
        )}
        <p className="sa-coverage-modal__hint">
          Same rules as the org snapshot. Select a location to open Location Performance.
        </p>

        {sortedRows.length === 0 ? (
          <p className="sa-coverage-modal__empty">No location coverage data available.</p>
        ) : (
          <div className="sa-location-budget-table-wrap">
            <table className="sa-location-budget-table sa-coverage-modal__table">
              <thead>
                <tr>
                  {sortHeader('location', 'Location')}
                  {sortHeader('coverage_expiring', 'Expiring', true)}
                  {sortHeader('no_coverage', 'No coverage', true)}
                  {sortHeader('coverage_expired', 'Expired', true)}
                  {sortHeader('warranty_count', 'Warranty', true)}
                  {sortHeader('lamc_count', 'LAMC', true)}
                  {sortHeader('camc_count', 'CAMC', true)}
                  {sortHeader('inhouse_count', 'In-house', true)}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.location_id || row.location_name}>
                    <td>
                      {onSelectLocation ? (
                        <button
                          type="button"
                          className="sa-coverage-modal__location"
                          onClick={() => onSelectLocation(row)}
                        >
                          {row.location_name}
                          {row.location_code && row.location_code !== 'N/A' && (
                            <span className="sa-coverage-modal__code">{row.location_code}</span>
                          )}
                        </button>
                      ) : (
                        <span className="sa-location-budget__name">
                          {row.location_name}
                          {row.location_code && row.location_code !== 'N/A' && (
                            <span className="sa-coverage-modal__code">{row.location_code}</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <CoverageCount value={row.coverage_expiring} tone="expiring" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.no_coverage} tone="nocov" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.coverage_expired} tone="expired" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.warranty_count} tone="mix" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.lamc_count} tone="mix" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.camc_count} tone="mix" />
                    </td>
                    <td className="num">
                      <CoverageCount value={row.inhouse_count} tone="mix" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>All locations</td>
                  <td className="num"><CoverageCount value={totals.coverage_expiring} tone="expiring" /></td>
                  <td className="num"><CoverageCount value={totals.no_coverage} tone="nocov" /></td>
                  <td className="num"><CoverageCount value={totals.coverage_expired} tone="expired" /></td>
                  <td className="num"><CoverageCount value={totals.warranty_count} tone="mix" /></td>
                  <td className="num"><CoverageCount value={totals.lamc_count} tone="mix" /></td>
                  <td className="num"><CoverageCount value={totals.camc_count} tone="mix" /></td>
                  <td className="num"><CoverageCount value={totals.inhouse_count} tone="mix" /></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
